import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import DefaultComponent from '../Components/DefaultComponent';
import {
  loadInitialConfiguration,
  saveConfigurationForCart,
  setErrorMessage,
  validateConfiguration
} from '../Reducers/ConfigurationSlice';
import { IComponentGroup, IConfiguration, IHint } from '../Interface/Interface';
import ConfigurationInfo from '../Components/ConfigurationInfo';
import { makeStyles } from 'tss-react/mui';
import {
  Box,
  Button,
  ButtonGroup,
  DialogActions,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  Theme,
  Typography,
  useMediaQuery
} from '@mui/material';
import Select from '@mui/material/Select';
import { t } from 'react-i18nify';
import SnackBar from '../Components/SnackBar';
import PriceBox from '../Components/PriceBox';
import { black, darkBlue, lightgray, white } from '../Constants/Colors';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import Progress from '../Components/Progress';
import createMessageInterface from '../Utils/createMessageInterface';
import { store } from '../store';
import { StickyGrid } from '../Components/StickyGrid';
import {
  AbsoluteDialog,
  useContainerSizeUpdate
} from '../Components/ContainerPositionProvider';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}>
      {value === index && (
        <Box sx={{ pt: 3 }}>
          <Typography component={'span'}>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

const useStyles = makeStyles()((theme) => {
  return {
    root: {
      margin: theme.spacing(2)
    },
    Components: {
      [theme.breakpoints.up('lg')]: {
        paddingRight: theme.spacing(4)
      },
      [theme.breakpoints.down('sm')]: {
        paddingBottom: theme.spacing(18)
      },
      width: '100%'
    },
    itemTitle: {
      paddingBottom: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        fontSize: '16px !important',
        paddingRight: theme.spacing(2)
      }
    },
    ConfigurationInfo: {
      height: 'fit-content',
      maxWidth: 'fit-content',
      marginTop: theme.spacing(3),
      padding: theme.spacing(4),
      border: `1px solid ${black}`
    },
    buttongroup: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'inherit',
      width: '100%',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'row'
      }
    },
    footer: {
      backgroundColor: white,
      position: 'fixed',
      bottom: '0',
      minWidth: '100%',
      right: '0',
      left: '0',
      padding: theme.spacing(2)
    },
    dialogBox: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      padding: 0
    },
    footerSummery: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'start',
      padding: 0,
      justifyContent: 'start'
    },
    footerSummeryCongigInfo: {
      textDecoration: 'underline',
      fontWeight: 'bold',
      pl: 1,
      color: darkBlue
    },
    footerButtongroup: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '16px',
      '& button': {
        flex: 1
      }
    },
    dialogOverlay: {
      '& .MuiDialog-paper': {
        maxWidth: 'none !important',
        maxHeight: 'none !important',
        width: '100% !important',
        height: '100% !important',
        margin: '0 !important',
        overflowY: 'inherit',
        backgroundColor: lightgray
      }
    },
    select: {
      width: '50%',
      textTransform: 'uppercase',
      fontFamily: 'Tahoma !important',
      fontSize: '14px !important',
      color: `${darkBlue} !important`
    },
    menuItem: {
      textTransform: 'uppercase',
      fontFamily: 'Tahoma !important',
      fontSize: '14px !important',
      color: `${darkBlue} !important`
    }
  };
});

const ConfiguratorScreen = () => {
  const dispatch = useAppDispatch();
  const groupedComponents: IComponentGroup[] | undefined = useAppSelector(
    (state) => state.configuration.groupedComponents
  );
  const configuration: IConfiguration | undefined = useAppSelector(
    (state) => state.configuration.configuration
  );
  const errorMessage = useAppSelector(
    (state) => state.configuration.errorMessage
  );

  const hints: IHint[] | undefined = useAppSelector(
    (state) => state.configuration.hints
  );
  const hasHints: boolean = hints ? hints.length > 0 : false;

  const isInvalidState = useAppSelector(
    (state) => state.configuration.invalidState
  );

  const { classes } = useStyles();

  const [activeTabValue, setActiveTabValue] = React.useState(0);
  const [open, setOpen] = React.useState(false);

  useEffect(() => {
    dispatch(loadInitialConfiguration());
    initAdminMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { setRootRef } = useContainerSizeUpdate(16, 16);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const onTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTabValue(newValue);
  };

  const ondropdownChange = (event: any) => {
    setActiveTabValue(event.target.value);
  };

  const onNextStepClick = () => {
    setActiveTabValue((prevStep) => prevStep + 1);
    window.scrollTo(0, 0);
  };

  const onAddToCartClick = async () => {
    if (!isInvalidState && !hasHints && configuration) {
      await dispatch(validateConfiguration());
      let configurationCode;
      const query = new URLSearchParams(location.search);
      if (query.has('_frombasket') && query.get('_frombasket') === '1') {
        configurationCode = configuration.code;
      }
      dispatch(saveConfigurationForCart(configurationCode));
    }
  };

  const initAdminMode = () => {
    const query = new URLSearchParams(location.search);
    if (query.has('_adminmode')) {
      const messageInterface = createMessageInterface();
      messageInterface.addListener('getConfiguration', handleAdminSave);
    }
  };

  const handleAdminSave = async (event: any) => {
    const query = new URLSearchParams(location.search);
    const adminMode = query.get('_adminmode');
    const configuration = store.getState().configuration.configuration;
    event.source.postMessage(
      { ...event.data, adminMode, configuration },
      event.origin
    );
  };

  const maxStep = groupedComponents?.length ? groupedComponents?.length - 1 : 0;
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm')
  );

  return (
    <Box ref={setRootRef} className={classes.root}>
      <Progress />

      <Grid container>
        <Grid item={true} lg={8} md={12} sm={12} className={classes.Components}>
          <Box sx={{ display: 'flex' }}>
            {isMobile && (
              <Select
                className={classes.select}
                value={activeTabValue}
                onChange={ondropdownChange}>
                {groupedComponents &&
                  groupedComponents.map(
                    (componentGroup: IComponentGroup, i: number) => (
                      <MenuItem
                        className={classes.menuItem}
                        value={i}
                        key={componentGroup.group.value}>
                        {componentGroup.group.translated_value}{' '}
                      </MenuItem>
                    )
                  )}
              </Select>
            )}
          </Box>

          {!isMobile && (
            <Tabs
              value={activeTabValue}
              onChange={onTabChange}
              aria-label="component group tabs">
              {groupedComponents &&
                groupedComponents.map((componentGroup: IComponentGroup) => (
                  <Tab
                    label={componentGroup.group.translated_value}
                    key={componentGroup.group.value}
                  />
                ))}
            </Tabs>
          )}

          {groupedComponents &&
            groupedComponents.map((componentGroup: IComponentGroup, i) => {
              return (
                <TabPanel
                  index={i}
                  key={componentGroup.group.value}
                  value={activeTabValue}>
                  {componentGroup.components.map((component, i) => {
                    if (
                      component.hiddenInFrontend ||
                      !component.options?.length
                    ) {
                      return null;
                    }
                    return (
                      <React.Fragment key={i}>
                        {i > 0 && <Divider />}
                        <DefaultComponent
                          component={component}
                          allowZeroAmount={component.is_multiselect}
                          multiselect={component.is_multiselect}
                          disabled={isInvalidState}
                        />
                      </React.Fragment>
                    );
                  })}
                </TabPanel>
              );
            })}
        </Grid>

        {configuration && !isMobile && (
          <StickyGrid
            bottomMargin={16}
            item={true}
            lg={4}
            md={12}
            sm={12}
            className={classes.ConfigurationInfo}
            sx={{
              position: 'sticky',
              right: 0
            }}>
            <ConfigurationInfo />
            <Box>
              <PriceBox />
              <ButtonGroup className={classes.buttongroup}>
                {activeTabValue < maxStep && (
                  <Button variant="contained" onClick={onNextStepClick}>
                    {t('nextStep')}
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={onAddToCartClick}
                  disabled={isInvalidState}
                  sx={{ backgroundColor: darkBlue, mt: 2 }}>
                  {t('addToCart')}
                </Button>
              </ButtonGroup>
            </Box>
          </StickyGrid>
        )}
        {configuration && isMobile && (
          <Grid sx={{ boxShadow: 5 }} className={classes.footer}>
            <Box className={classes.footerButtongroup} sx={{ mb: 1 }}>
              <Button
                variant="text"
                className={classes.footerSummery}
                onClick={handleClickOpen}>
                <Typography
                  sx={{
                    textDecoration: 'underline',
                    fontWeight: 'bold',
                    color: darkBlue,
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    lineHeight: 1
                  }}>
                  <ArrowRightIcon sx={{ color: darkBlue, ml: '-10px' }} />
                  {t('configurationHeadline')}
                </Typography>
              </Button>
              <PriceBox />

              <AbsoluteDialog
                open={open}
                onClose={handleClose}
                className={classes.dialogOverlay}>
                <DialogActions sx={{ padding: '0px' }}>
                  <IconButton sx={{ color: 'black' }} onClick={handleClose}>
                    <CloseIcon />
                  </IconButton>
                </DialogActions>
                <ConfigurationInfo />
              </AbsoluteDialog>
            </Box>

            <ButtonGroup
              sx={{
                justifyContent:
                  activeTabValue === maxStep ? 'end' : 'space-between'
              }}
              className={classes.footerButtongroup}>
              {activeTabValue < maxStep && (
                <Button variant="contained" onClick={onNextStepClick}>
                  {t('nextStep')}
                </Button>
              )}
              <Button
                variant="contained"
                onClick={onAddToCartClick}
                disabled={isInvalidState}
                sx={{ backgroundColor: darkBlue }}>
                {t('addToCart')}
              </Button>
            </ButtonGroup>
          </Grid>
        )}
        <SnackBar
          message={errorMessage}
          onCloseMessage={() => dispatch(setErrorMessage(undefined))}
        />
      </Grid>
    </Box>
  );
};

export default ConfiguratorScreen;
